import { useMemo, useState } from 'react';
import { format, isValid } from 'date-fns';
import { uk } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';

import IconChevronDown from '@/assets/svg/icon-chevron-down.svg?react';
import IconClose from '@/assets/svg/icon-close.svg?react';
import IconSearchParams from '@/assets/svg/icon-search-params.svg?react';
import IconSearch from '@/assets/svg/icon-search.svg?react';
import IconSearchDatetime from '@/assets/svg/search-datatime.svg?react';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useAuthStore } from '@/stores/authStore';

import { USER_ROLES } from '@/constants/global-constants';
import {
  SEARCH_PARAMS_FILES,
  SEARCH_PARAMS_FILES_LIST,
} from '@/constants/search-params';

interface FileSearchProps {
  onSearch: (params: {
    field: string;
    value: string;
    searchId: string;
  }) => void;
}

export function FileSearch({ onSearch }: FileSearchProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useAuthStore();

  const searchParamsByRole = useMemo(() => {
    return SEARCH_PARAMS_FILES_LIST.filter((param) => {
      return (
        user?.role === USER_ROLES.CONTROLLER.value ||
        SEARCH_PARAMS_FILES.DIRECTORY.id !== param.id
      );
    });
  }, [user?.role]);

  const selectedItem = useMemo(() => {
    return (
      searchParamsByRole.find((param) => param.id === selectedItemId) || null
    );
  }, [selectedItemId, searchParamsByRole]);

  const isDateField = useMemo(() => {
    if (!selectedItem) return false;
    return (
      selectedItem.id === SEARCH_PARAMS_FILES.UPLOAD_DATE.id ||
      selectedItem.id === SEARCH_PARAMS_FILES.FILMED_DATE.id
    );
  }, [selectedItem]);

  const handleSearch = () => {
    if (!selectedItem) {
      return;
    }
    setIsLoading(true);
    if (isDateField && dateRange) {
      // Format date range as JSON string for search
      const formattedDateRange = JSON.stringify({
        from: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : null,
        to: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : null,
      });
      onSearch({
        field: selectedItem.field,
        value: formattedDateRange,
        searchId: selectedItem.id,
      });
    } else {
      onSearch({
        field: selectedItem.field,
        value: searchValue,
        searchId: selectedItem.id,
      });
    }
    setIsLoading(false);
  };

  const handleSelectItem = (id: string) => {
    setSelectedItemId(id);
    // Reset values when changing search parameter
    setSearchValue('');
    setDateRange(undefined);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setDateRange(undefined);
    onSearch({
      field: selectedItem?.field || '',
      value: '',
      searchId: selectedItem?.id || '',
    });
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range);

    if (range?.from) {
      // Update search value for display purposes
      let displayText = format(range.from, 'dd.MM.yyyy', { locale: uk });

      if (range.to) {
        displayText += ` - ${format(range.to, 'dd.MM.yyyy', { locale: uk })}`;
        // Close the date picker when a complete range is selected
        setIsDatePickerOpen(false);
      } else if (!range.to && isValid(range.from)) {
        // If only start date is selected
        displayText += ' - ...';
      }

      setSearchValue(displayText);
    } else {
      setSearchValue('');
    }
  };

  const renderSearchDropdownLabel = () => {
    return isMobile ? (
      <IconSearchParams className="h-[34px] w-[34px]" />
    ) : (
      'Параметри пошуку'
    );
  };

  return (
    <div className="relative flex h-[56px] items-center rounded-md bg-white shadow-sm sm:h-auto sm:p-2.5 sm:pr-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="relative z-10 flex items-center justify-between whitespace-nowrap rounded-md py-3 pl-2.5 pr-2.5 text-base font-normal text-primary hover:cursor-pointer hover:bg-blue-200 sm:min-w-[200px]">
            {searchParamsByRole.find((param) => param.id === selectedItemId)
              ?.label || renderSearchDropdownLabel()}
            <IconChevronDown className="ml-1 h-3.5 w-3.5 fill-transparent stroke-primary" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="mt-4 max-h-[300px] w-56 overflow-y-auto p-1 text-lg">
          {searchParamsByRole.map((param) => (
            <DropdownMenuItem
              key={param.id}
              onClick={() => handleSelectItem(param.id)}
              className="px-4 py-3.5"
            >
              <span className="text-base text-primary">{param.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {isDateField ? (
        <div className="relative flex-1">
          <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
            <PopoverTrigger asChild>
              <div className="relative mr-1 flex h-12 flex-1 cursor-pointer items-center rounded-none border-0 border-l border-neutral-200 bg-transparent !pr-8 pl-4 text-base outline-none focus-visible:ring-0 md:text-sm">
                {searchValue || 'Оберіть діапазон дат'}
                <IconSearchDatetime className="ml-2 h-5 w-5 fill-primary" />
              </div>
            </PopoverTrigger>
            <PopoverContent className="z-50 w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={handleDateRangeSelect}
                locale={uk}
                initialFocus
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          {searchValue && (
            <Button
              variant="link"
              size="icon"
              onClick={handleClearSearch}
              className="absolute right-[10px] top-1/2 flex -translate-y-1/2 items-center justify-center bg-transparent [&:hover_svg]:fill-destructive"
            >
              <IconClose className="h-3.5 w-3.5 fill-gray-500" />
            </Button>
          )}
        </div>
      ) : (
        <input
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Оберіть параметрі пошуку"
          className="relative mr-1 h-12 flex-1 rounded-none border-0 border-l border-neutral-200 bg-transparent !pr-8 pl-4 text-base outline-none focus-visible:ring-0 md:text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          disabled={isLoading}
        />
      )}

      {searchValue && !isDateField && (
        <Button
          variant="link"
          size="icon"
          onClick={handleClearSearch}
          className="absolute right-[52px] top-1/2 flex -translate-y-1/2 items-center justify-center bg-transparent sm:right-[130px] [&:hover_svg]:fill-destructive"
        >
          <IconClose className="h-3.5 w-3.5 fill-gray-500" />
        </Button>
      )}
      <Button
        className="h-full w-[48px] rounded-l-none text-white sm:h-[50px] sm:w-[110px] sm:rounded-l-md"
        onClick={handleSearch}
      >
        {!isMobile ? 'Шукати' : <IconSearch className="h-[28px] fill-white" />}
      </Button>
    </div>
  );
}
